import { SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  ChallengeApplicationStats,
  GET_CHALLENGE_APPLICATION_STATS,
} from '@frontend/data-access';
import { Card, Select, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useMemo, useState } from 'react';

const ChallengeByApplicationStats = ({ className }: { className?: string }) => {
  const { t } = useTranslation('admin');
  const [filter, setFilter] = useState<string[]>([]);
  const [registrationStatus, setRegistrationStatus] = useState<
    'ALL' | 'ACTIVE' | 'ENDED'
  >('ALL');
  const [currentPage, setCurrentPage] = useState(1);

  const stateOptions = useMemo(
    () => [
      { value: 'ALL', label: t('all') },
      { value: 'ACTIVE', label: t('registration_open') },
      { value: 'ENDED', label: t('registration_closed') },
    ],
    [t]
  );
  const { loading, data } = useQuery<{
    getApplicationByRegistration: ChallengeApplicationStats[];
  }>(GET_CHALLENGE_APPLICATION_STATS, {
    variables: { type: 'OPEN', order: true, limit: 100 },
  });

  const renderStatus = (state: string) => {
    switch (state) {
      case 'ACTIVE':
        return <div>{t('registration_open')}</div>;
      case 'ENDED':
        return <div>{t('registration_closed')}</div>;

      default:
        break;
    }
  };

  const columns: ColumnsType<ChallengeApplicationStats> = [
    {
      title: t('no'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (_, __, index) => {
        const actualIndex = (currentPage - 1) * 10 + index + 1;
        return `${actualIndex}.`;
      },
    },
    {
      title: t('challenge_name'),
      dataIndex: 'challenge',
      key: 'challenge',
      width: 300,
      render: (_, { challenge, id }) => (
        <Link href={`/entrepreneurship/challenge/${id}`}>{challenge}</Link>
      ),
    },
    {
      title: t('status'),
      dataIndex: 'registration',
      key: 'registration',
      render: (record) => <div>{renderStatus(record)}</div>,
    },
    {
      title: t('submitted_applications'),
      dataIndex: 'applications',
      key: 'applications',
    },
    {
      title: t('companies'),
      dataIndex: 'companies',
      key: 'companies',
    },
    {
      title: t('profile_managers'),
      dataIndex: 'profileManagers',
      key: 'profileManagers',
      render: (_, { profileOwners }) => profileOwners?.length || '0',
    },
  ];

  const filteredData = useMemo(() => {
    if (!data?.getApplicationByRegistration) {
      return [];
    }

    let filtered = data.getApplicationByRegistration;

    if (filter.length > 0) {
      filtered = filtered.filter((item) =>
        filter.some((text) =>
          item.challenge.toLowerCase().includes(text.toLowerCase())
        )
      );
    }

    if (registrationStatus !== 'ALL') {
      filtered = filtered.filter(
        (item) => item.registration === registrationStatus
      );
    }

    return filtered;
  }, [data, filter, registrationStatus]);

  const challengeOptions = useMemo(() => {
    if (!data?.getApplicationByRegistration) {
      return [];
    }

    return data.getApplicationByRegistration.map((item) => ({
      label: item.challenge,
      value: item.challenge,
    }));
  }, [data]);

  const handleRegistrationStatusChange = (
    status: 'ALL' | 'ACTIVE' | 'ENDED'
  ) => {
    setRegistrationStatus(status);
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
      className={`${className}`}
    >
      <div className="flex gap-4 mb-6">
        <Select
          onChange={setFilter}
          options={challengeOptions}
          value={filter}
          placeholder={t('search_challenge_name')}
          allowClear
          mode="multiple"
          className="!w-56"
          maxTagCount="responsive"
          suffixIcon={<SearchOutlined />}
          optionFilterProp="label"
        />
        <Select
          style={{ width: 180 }}
          placeholder={t('by_challenge_state')}
          options={stateOptions}
          onChange={handleRegistrationStatusChange}
        />
      </div>
      <Table
        rowKey="id"
        columns={columns}
        loading={loading}
        className={className}
        dataSource={filteredData}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
          current: currentPage,
          onChange: (page) => setCurrentPage(page),
          total: filteredData.length,
        }}
      />
    </Card>
  );
};

export default ChallengeByApplicationStats;
